// src/components/analytics-dashboard/ChartsSection.js
import { __ } from '@wordpress/i18n';
import {
  ArcElement,
  CategoryScale,
  Chart,
  DoughnutController,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js';
import { useEffect, useRef, useState } from 'react';

// Register required components globally
Chart.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  DoughnutController,
  ArcElement,
  Tooltip,
  Legend
);

/**
 * Function to get CSS variable value
 */
const getCssVariableValue = (variableName) => {
  // Fallback values in case the CSS variable is not found
  const fallbackColors = {
    '--hvnly-primary': '#6C60FE',
    '--hvnly-success': '#00B46A',
    '--hvnly-status-pending': '#FFB507',
    '--hvnly-bg-white': '#FFFFFF',
    '--hvnly-accent': '#FF9AA2',
    '--hvnly-secondary': '#764ba2',
  };
  
  if (typeof window === 'undefined') {
    return fallbackColors[variableName] || '#000000';
  }
  
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(variableName)
    .trim();
    
  return value || fallbackColors[variableName] || '#000000';
};

/**
 * Charts Section Component
 */
const ChartsSection = ({ statsData, timeRange }) => {
  const visitorsChartRef = useRef(null);
  const devicesChartRef = useRef(null);
  const [visitorsChart, setVisitorsChart] = useState(null);
  const [devicesChart, setDevicesChart] = useState(null);
  const [cssColors, setCssColors] = useState(null);

  // Get CSS variable values on component mount
  useEffect(() => {
    const primaryColor = getCssVariableValue('--hvnly-primary');
    const successColor = getCssVariableValue('--hvnly-success');
    const pendingColor = getCssVariableValue('--hvnly-status-pending');
    const bgWhite = getCssVariableValue('--hvnly-bg-white');
    const accentColor = getCssVariableValue('--hvnly-accent');
    const secondaryColor = getCssVariableValue('--hvnly-secondary');
    
    setCssColors({
      primary: primaryColor,
      success: successColor,
      pending: pendingColor,
      bgWhite: bgWhite,
      accent: accentColor,
      secondary: secondaryColor,
      primaryLight: `${primaryColor}20`, // Add transparency
    });
  }, []);

  useEffect(() => {
    if (!cssColors) return; // Wait for colors to be loaded
    
    // Clean up existing charts
    if (visitorsChart) {
      visitorsChart.destroy();
    }
    if (devicesChart) {
      devicesChart.destroy();
    }

    // Visitors Chart
    if (visitorsChartRef.current) {
      const ctx = visitorsChartRef.current.getContext('2d');
      
      // Get data based on selected time range
      const data = statsData.visitorsData[timeRange];
      const labels = timeRange === 'daily' 
        ? [__('Mon', 'havenlytics'), __('Tue', 'havenlytics'), __('Wed', 'havenlytics'), __('Thu', 'havenlytics'), __('Fri', 'havenlytics'), __('Sat', 'havenlytics'), __('Sun', 'havenlytics')]
        : timeRange === 'weekly'
        ? [__('Week 1', 'havenlytics'), __('Week 2', 'havenlytics'), __('Week 3', 'havenlytics'), __('Week 4', 'havenlytics'), __('Week 5', 'havenlytics'), __('Week 6', 'havenlytics'), __('Week 7', 'havenlytics')]
        : [__('Jan', 'havenlytics'), __('Feb', 'havenlytics'), __('Mar', 'havenlytics'), __('Apr', 'havenlytics'), __('May', 'havenlytics'), __('Jun', 'havenlytics'), __('Jul', 'havenlytics')];
      
      const newVisitorsChart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: labels,
          datasets: [{
            label: __('Visitors', 'havenlytics'),
            data: data,
            borderColor: cssColors.primary,
            backgroundColor: cssColors.primaryLight,
            tension: 0.4,
            fill: true,
            pointBackgroundColor: cssColors.primary,
            pointBorderColor: cssColors.bgWhite,
            pointBorderWidth: 2,
            pointRadius: 5,
            pointHoverRadius: 8
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              backgroundColor: cssColors.bgWhite,
              titleColor: cssColors.primary,
              bodyColor: cssColors.secondary,
              borderColor: cssColors.primary,
              borderWidth: 1,
              displayColors: false,
              callbacks: {
                label: function(context) {
                  return `${__('Visitors', 'havenlytics')}: ${context.parsed.y}`;
                }
              }
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              grid: {
                color: 'rgba(0, 0, 0, 0.05)',
                drawBorder: false
              },
              ticks: {
                color: cssColors.secondary
              }
            },
            x: {
              grid: {
                display: false
              },
              ticks: {
                color: cssColors.secondary
              }
            }
          }
        }
      });
      
      setVisitorsChart(newVisitorsChart);
    }
    
    // Devices Chart
    if (devicesChartRef.current) {
      const ctx = devicesChartRef.current.getContext('2d');
      
      const newDevicesChart = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: [__('Mobile', 'havenlytics'), __('Desktop', 'havenlytics'), __('Tablet', 'havenlytics')],
          datasets: [{
            data: [statsData.deviceData.mobile, statsData.deviceData.desktop, statsData.deviceData.tablet],
            backgroundColor: [
              cssColors.primary,
              cssColors.success,
              cssColors.pending
            ],
            borderColor: cssColors.bgWhite,
            borderWidth: 2,
            hoverOffset: 15,
            hoverBackgroundColor: [
              `${cssColors.primary}DD`,
              `${cssColors.success}DD`,
              `${cssColors.pending}DD`
            ]
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'bottom',
              labels: {
                usePointStyle: true,
                padding: 20,
                color: cssColors.secondary,
                font: {
                  family: 'Inter, sans-serif'
                }
              }
            },
            tooltip: {
              backgroundColor: cssColors.bgWhite,
              titleColor: cssColors.primary,
              bodyColor: cssColors.secondary,
              borderColor: cssColors.primary,
              borderWidth: 1,
              callbacks: {
                label: function(context) {
                  return `${context.label}: ${context.parsed}%`;
                }
              }
            }
          },
          cutout: '70%',
          animation: {
            animateScale: true,
            animateRotate: true
          }
        }
      });
      
      setDevicesChart(newDevicesChart);
    }

    // Cleanup function
    return () => {
      if (visitorsChart) {
        visitorsChart.destroy();
      }
      if (devicesChart) {
        devicesChart.destroy();
      }
    };
  }, [statsData, timeRange, cssColors]); // Added cssColors to dependency array

  // Show loading state while colors are being loaded
  if (!cssColors) {
    return (
      <div className="hvnly-charts-section">
        <div className="hvnly-chart-card">
          <div className="hvnly-chart-header">
            <h2 className="hvnly-chart-title">{__('Visitor Analytics', 'havenlytics')}</h2>
          </div>
          <div className="hvnly-chart-container" style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
            <i className="fas fa-spinner fa-spin" style={{color: 'var(--hvnly-primary)'}}></i>
          </div>
        </div>
        
        <div className="hvnly-chart-card">
          <div className="hvnly-chart-header">
            <h2 className="hvnly-chart-title">{__('Device Distribution', 'havenlytics')}</h2>
          </div>
          <div className="hvnly-chart-container" style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
            <i className="fas fa-spinner fa-spin" style={{color: 'var(--hvnly-primary)'}}></i>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="hvnly-charts-section">
      <div className="hvnly-chart-card">
        <div className="hvnly-chart-header">
          <h2 className="hvnly-chart-title">{__('Visitor Analytics', 'havenlytics')}</h2>
          <div className="hvnly-chart-actions">
            <button className="hvnly-chart-action-btn">
              <i className="fas fa-download"></i>
              {__('Export', 'havenlytics')}
            </button>
            <button className="hvnly-chart-action-btn">
              <i className="fas fa-expand"></i>
            </button>
          </div>
        </div>
        <div className="hvnly-chart-container">
          <canvas ref={visitorsChartRef}></canvas>
        </div>
      </div>
      
      <div className="hvnly-chart-card">
        <div className="hvnly-chart-header">
          <h2 className="hvnly-chart-title">{__('Device Distribution', 'havenlytics')}</h2>
          <div className="hvnly-chart-actions">
            <button className="hvnly-chart-action-btn">
              <i className="fas fa-download"></i>
            </button>
          </div>
        </div>
        <div className="hvnly-chart-container">
          <canvas ref={devicesChartRef}></canvas>
        </div>
      </div>
    </div>
  );
};

export default ChartsSection;